import { useState } from "react";
import {
  Bookmark01Icon,
  Comment01Icon,
  CursorMagicSelection01Icon,
  Edit02Icon,
  FavouriteIcon,
  Message01Icon,
  MouseScroll01Icon,
  News01Icon,
  Search01Icon,
  ThumbsUpIcon,
  UserAdd01Icon,
  UserSearch01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Check, Copy } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { IconSvgElement } from "@hugeicons/react";
import { cn } from "@/common/utils";
import { useCopy } from "@/landing/hooks/useCopy";
import { useInstallCopy } from "@/landing/hooks/useInstallCopy";
import { FeatureCardGrid } from "@/landing/ui/FeatureCard";
import { SectionEyebrow } from "@/landing/ui/SectionEyebrow";
import ClayLogo from "@/landing/wab/assets/logos/clay-logo-ink.svg?url";
import DeeplineLogo from "@/landing/wab/assets/logos/deepline-logo-ink.svg?url";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

/* ------------------------------------------------------------------ */
/*  Agent prompt + direct install                                      */
/* ------------------------------------------------------------------ */

const NPM_COMMAND = "npm install -g @degausai/wonda";

function AgentPromptBox() {
  const [copiedPrompt, copyPrompt] = useCopy();
  const { t } = useTranslation();
  const agentPrompt = t("howItWorks.agentPrompt");

  return (
    <div>
      {/* Quoted agent prompt */}
      <div
        className="flex items-start justify-between rounded-lg px-3.5 py-2.5"
        style={{
          border: "1px solid var(--hair)",
          borderLeft: "2px solid var(--green)",
        }}
      >
        <span
          className="text-sm leading-snug italic"
          style={{ color: "var(--ink)" }}
        >
          &ldquo;{agentPrompt}&rdquo;
        </span>
        <button
          type="button"
          onClick={() => copyPrompt(agentPrompt)}
          className={cn(
            "mt-0.5 ml-2 flex shrink-0 items-center justify-center rounded-md p-1 transition-colors duration-200",
            copiedPrompt
              ? "text-[var(--green)]"
              : "text-[var(--steward-muted)] hover:text-[#3f4347]",
          )}
          aria-label={
            copiedPrompt ? t("howItWorks.copied") : t("howItWorks.copyPrompt")
          }
        >
          {copiedPrompt ? (
            <Check className="size-3.5" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 1 — Download Wonda CLI (For agents / For humans tabs)         */
/* ------------------------------------------------------------------ */

const CLI_TABS = [
  { id: "agents", label: "For agents" },
  { id: "humans", label: "For humans" },
] as const;

function CliInstall() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"agents" | "humans">("agents");
  const [copied, copy] = useInstallCopy("how_it_works");

  return (
    <div className="mt-5">
      {/* Tab switcher */}
      <div
        className="mb-4 flex items-center gap-5 border-b"
        style={{ borderColor: "var(--hair)" }}
      >
        {CLI_TABS.map((item) => {
          const isActive = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={cn(
                "relative -mb-px border-b-2 pb-2 text-xs font-medium transition-colors",
                isActive
                  ? "text-[var(--ink)]"
                  : "border-transparent text-[var(--steward-muted)] hover:text-[#3f4347]",
              )}
              style={isActive ? { borderColor: "var(--green)" } : undefined}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Active panel */}
      {tab === "agents" ? (
        <AgentPromptBox />
      ) : (
        <div>
          <p className="mb-2 text-xs" style={{ color: "var(--steward-muted)" }}>
            Install the CLI directly:
          </p>
          <div
            className="flex items-center justify-between rounded-lg px-3.5 py-2.5"
            style={{
              border: "1px solid var(--hair)",
              borderLeft: "2px solid var(--green)",
            }}
          >
            <code
              className="min-w-0 truncate font-mono text-sm"
              style={{ color: "var(--ink)" }}
            >
              {NPM_COMMAND}
            </code>
            <button
              type="button"
              onClick={() => copy(NPM_COMMAND)}
              className={cn(
                "ml-2 flex shrink-0 items-center justify-center rounded-md p-1 transition-colors duration-200",
                copied
                  ? "text-[var(--green)]"
                  : "text-[var(--steward-muted)] hover:text-[#3f4347]",
              )}
              aria-label={
                copied ? t("howItWorks.copied") : t("howItWorks.copyCommand")
              }
            >
              {copied ? (
                <Check className="size-3.5" />
              ) : (
                <Copy className="size-3.5" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 2 — Account hub graph                                         */
/* ------------------------------------------------------------------ */

function AccountHub() {
  return (
    // flex-1 + justify-center vertically centers the WAB browser in the card.
    <div className="mt-5 flex flex-1 flex-col items-center justify-center">
      <img
        src={WabDockIcon}
        alt="Wonda Automation Browser"
        className="size-16 rounded-2xl shadow-none"
      />
      <span
        className="mt-3 max-w-[16rem] text-center text-xs leading-snug"
        style={{ color: "#3f4347" }}
      >
        #1 antidetect browser for social media to keep your accounts safe
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step 3 — GTM workflow logos                                        */
/* ------------------------------------------------------------------ */

const GTM_LOGOS = [
  { src: ClayLogo, alt: "Clay" },
  { src: DeeplineLogo, alt: "Deepline" },
];

// The manual GTM motions Claude takes over once connected.
const STEP3_FLOWS: { label: string; icon: IconSvgElement }[] = [
  { label: "Connect", icon: UserAdd01Icon },
  { label: "Message", icon: Message01Icon },
  { label: "Comment", icon: Comment01Icon },
  { label: "Like", icon: ThumbsUpIcon },
  { label: "React", icon: FavouriteIcon },
  { label: "Post", icon: Edit02Icon },
  { label: "Scroll", icon: MouseScroll01Icon },
  { label: "Search", icon: Search01Icon },
  { label: "Extract", icon: CursorMagicSelection01Icon },
  { label: "Read", icon: News01Icon },
  { label: "Analyze", icon: UserSearch01Icon },
  { label: "Save", icon: Bookmark01Icon },
];

/* ------------------------------------------------------------------ */
/*  Main section                                                       */
/* ------------------------------------------------------------------ */

export function Wab2HowItWorks() {
  // The cells fill pure white so they sit a clear step above the warm
  // --ground-elevated band.
  const cardClass =
    "group relative flex h-full flex-col p-6 transition-colors bg-[var(--surface)] hover:bg-[#f0f0ec]";
  const numberStyle = { background: "var(--green)", color: "white" };

  return (
    <section
      id="how-it-works"
      className="scroll-mt-16 border-y border-[var(--hair)] bg-[var(--ground-elevated)] px-4 py-14 sm:px-6 md:py-20"
    >
      <motion.div
        className="mx-auto max-w-5xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="mb-10 flex flex-col items-center text-center">
          <SectionEyebrow index="01">How it works</SectionEyebrow>
          <h2
            className="mt-5 text-2xl font-semibold tracking-[-0.03em] text-balance sm:text-3xl lg:text-4xl"
            style={{ color: "var(--ink)" }}
          >
            Set it up in 30 seconds
          </h2>
        </div>

        <FeatureCardGrid
          cols="sm:grid-cols-3"
          className="shadow-[var(--shadow-card)]"
        >
          {/* Step 1 — Download Wonda CLI */}
          <div className={cardClass}>
            <div className="flex items-center gap-3">
              <span
                className="flex size-7 items-center justify-center rounded-full text-xs font-bold"
                style={numberStyle}
              >
                1
              </span>
              <h3
                className="text-sm font-semibold"
                style={{ color: "var(--ink)" }}
              >
                Your agent downloads Wonda
              </h3>
            </div>
            <CliInstall />
          </div>

          {/* Step 2 — Securely sign in to your accounts */}
          <div className={cardClass}>
            <div className="flex items-center gap-3">
              <span
                className="flex size-7 items-center justify-center rounded-full text-xs font-bold"
                style={numberStyle}
              >
                2
              </span>
              <h3
                className="text-sm font-semibold"
                style={{ color: "var(--ink)" }}
              >
                Securely sign in to your accounts
              </h3>
            </div>
            <AccountHub />
          </div>

          {/* Step 3 — Scale your voice with Claude */}
          <div className={cardClass}>
            <div className="flex items-center gap-3">
              <span
                className="flex size-7 items-center justify-center rounded-full text-xs font-bold"
                style={numberStyle}
              >
                3
              </span>
              <h3
                className="text-sm font-semibold"
                style={{ color: "var(--ink)" }}
              >
                Claude can now do everything you&apos;re doing manually
              </h3>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {STEP3_FLOWS.map((flow) => (
                <span
                  key={flow.label}
                  className="flex items-center gap-1.5 border px-2.5 py-1 text-xs font-medium"
                  style={{
                    borderColor: "var(--hair-strong)",
                    color: "#3f4347",
                  }}
                >
                  <HugeiconsIcon
                    icon={flow.icon}
                    className="size-3.5 text-[var(--green)]"
                    strokeWidth={1.7}
                  />
                  {flow.label}
                </span>
              ))}
            </div>
            <div className="mt-auto pt-5">
              <p className="text-xs" style={{ color: "var(--steward-muted)" }}>
                Available by API for your GTM &amp; Marketing stack
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
                {GTM_LOGOS.map((logo) => (
                  <img
                    key={logo.alt}
                    src={logo.src}
                    alt={logo.alt}
                    className="h-5 w-auto opacity-70"
                  />
                ))}
              </div>
            </div>
          </div>
        </FeatureCardGrid>
      </motion.div>
    </section>
  );
}
